import type { ISchedule, ResourceType } from "@gettactic/api";
import { getDateFromSchedule } from "@gettactic/helpers/src/date-time/getTimeFromSchedule";

/**
 * Categories the bulk-cancel list groups reservations under. Resource types
 * that read the same to a person are collapsed together (`desk`/`workspace`,
 * the two parking types) so the "cancel a whole category" action matches how
 * people talk about their bookings.
 */
export type ReservationCategory = "desk" | "meeting_room" | "parking" | "other";

const CATEGORY_BY_RESOURCE_TYPE: Partial<
  Record<ResourceType, ReservationCategory>
> = {
  desk: "desk",
  workspace: "desk",
  meeting_room: "meeting_room",
  parking_lot: "parking",
  parking_space: "parking"
};

/** Display order of the categories within a day. */
export const RESERVATION_CATEGORY_ORDER: ReservationCategory[] = [
  "desk",
  "meeting_room",
  "parking",
  "other"
];

export interface ReservationCategoryGroup {
  category: ReservationCategory;
  schedules: ISchedule[];
}

export interface ReservationDayGroup {
  /** `yyyy-MM-dd` in the reservation's own office timezone. */
  date: string;
  categories: ReservationCategoryGroup[];
  /** Every schedule of the day, flattened — backs the day-level select-all. */
  schedules: ISchedule[];
}

export const getReservationCategory = (
  schedule: ISchedule
): ReservationCategory =>
  CATEGORY_BY_RESOURCE_TYPE[schedule.resource?.type] ?? "other";

/**
 * The day a reservation belongs to.
 *
 * Schedule datetimes come back as office wall-clock with a cosmetic trailing
 * `Z` (see `formatApiDatetime`), so they are already in the office's own time.
 * Converting them again would shift a booking by the viewer's offset and can
 * file it under the wrong day.
 */
export const getReservationDate = (schedule: ISchedule): string =>
  getDateFromSchedule(schedule.start);

/**
 * Groups reservations by day, then by category, both in display order.
 *
 * Days are sorted ascending and duplicate schedule ids are dropped — the same
 * schedule can arrive more than once when it holds several slots for the
 * caller.
 */
export const groupReservations = (
  schedules: readonly ISchedule[]
): ReservationDayGroup[] => {
  const seenIds = new Set<string>();
  const byDate = new Map<string, ISchedule[]>();

  for (const schedule of schedules) {
    if (!schedule.start || seenIds.has(schedule.id)) continue;
    seenIds.add(schedule.id);
    const date = getReservationDate(schedule);
    const existing = byDate.get(date);
    if (existing) {
      existing.push(schedule);
    } else {
      byDate.set(date, [schedule]);
    }
  }

  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, daySchedules]) => {
      const sorted = [...daySchedules].sort((a, b) =>
        a.start.localeCompare(b.start)
      );
      const categories = RESERVATION_CATEGORY_ORDER.map((category) => ({
        category,
        schedules: sorted.filter(
          (schedule) => getReservationCategory(schedule) === category
        )
      })).filter((group) => group.schedules.length > 0);

      return { date, categories, schedules: sorted };
    });
};
